import React, { FC } from 'react';
import { Box, Paper, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAppSelector } from '../hooks';
import { postGlassSx } from '../theme/glass';

interface PostCardSkeletonProps {
  /** Mirrors PostCard: `compact` = summary card; `expanded` = full content card. */
  variant?: 'compact' | 'expanded';
}

// MUI's wave shimmer animates a ::after gradient; pulse animates the root.
// Freeze both when the user prefers reduced motion.
const reducedMotionSx = {
  '@media (prefers-reduced-motion: reduce)': {
    '& .MuiSkeleton-root, & .MuiSkeleton-root::after': { animation: 'none' },
  },
};

const pillSkeleton = (width: number) => (
  <Skeleton animation="wave" variant="rounded" width={width} height={24} sx={{ borderRadius: 99 }} />
);

/**
 * Placeholder that keeps PostCard's shell (radius, border, spacing) so incoming
 * posts shimmer into the exact slots they'll occupy while the page loads.
 */
const PostCardSkeleton: FC<PostCardSkeletonProps> = ({ variant = 'compact' }) => {
  const theme = useTheme();
  const glassSettings = useAppSelector(state => state.glassSettings);
  // Match PostCard's surface so posts don't flip solid↔glass as they load in.
  const glass = postGlassSx(theme.tokens, theme.palette.mode, glassSettings);

  const header = (
    <Box sx={{ display: 'flex', gap: variant === 'expanded' ? 1.5 : 1, alignItems: 'flex-start' }}>
      <Skeleton animation="wave" variant="circular" width={40} height={40} sx={{ flexShrink: 0 }} />
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Skeleton animation="wave" height={22} width="72%" sx={{ mb: 0.5 }} />
        <Skeleton animation="wave" height={14} width="42%" />
      </Box>
    </Box>
  );

  if (variant === 'expanded') {
    return (
      <Paper
        variant="outlined"
        aria-hidden
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: theme.tokens.layout.contentMaxWidth,
          margin: '0 auto',
          overflow: 'clip',
          borderRadius: 'var(--sh-xl)',
          p: 2,
          mb: 3,
          backgroundColor: theme.palette.background.post,
          border: `${theme.tokens.borders.widthThin} ${theme.tokens.borders.style} ${theme.tokens.colors.cardBorder}`,
          ...glass,
          ...reducedMotionSx,
        }}
      >
        <Box sx={{ mb: 2 }}>
          {header}
          <Skeleton animation="wave" variant="rounded" height={120} sx={{ mt: 1.5, borderRadius: 'var(--sh-md)' }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          {pillSkeleton(72)}
          {pillSkeleton(64)}
          <Box sx={{ ml: 'auto' }}>
            <Skeleton animation="wave" variant="circular" width={20} height={20} />
          </Box>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      variant="outlined"
      aria-hidden
      sx={{
        display: 'flex',
        width: 'auto',
        overflow: 'clip',
        borderRadius: 'var(--sh-xl)',
        p: 1,
        mb: 2,
        backgroundColor: theme.palette.background.post,
        border: `${theme.tokens.borders.widthThin} ${theme.tokens.borders.style} ${theme.tokens.colors.cardBorder}`,
        ...glass,
        ...reducedMotionSx,
      }}
    >
      <Box sx={{ p: 1.25, pb: 1, flexGrow: 1 }}>
        {header}
        <Box sx={{ display: 'flex', mt: 1.25, alignItems: 'center', gap: 1 }}>
          {pillSkeleton(64)}
          {pillSkeleton(56)}
          <Box sx={{ ml: 'auto', pr: 0.5 }}>
            <Skeleton animation="wave" variant="circular" width={16} height={16} />
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default PostCardSkeleton;
